import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Card, Col, Container, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";

import Breadcrumbs from "../../../../Common/Breadcrumbs";
import ShimmerLoader from "../../../../Common/ShimmerLoader";
import { SuccessModal } from "../../../../Common/SuccessModal";

import AdCampaignPreview from "./components/AdCampaignPreview";
import StepBasicInfo from "./components/StepBasicInfo";

import type { AdCampaignFormData } from "../../../../api/ads/types";

import { useAdCampaignForm } from "./hooks/useAdCampaignForm";
import { getAdCampaignById, updateAdCampaign } from "../../../../api/ads";

import "./CreateCampaign.css";
import mockCreativeThumb from "assets/images/products/32/img-1.png";

type CampaignPageMode = "create" | "edit" | "view";

type CreateCampaignProps = {
  mode?: CampaignPageMode;
};

const addDaysISO = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const getTodayLocalISODate = () => addDaysISO(0);

const CreateCampaign: React.FC<CreateCampaignProps> = ({ mode = "create" }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const today = useMemo(() => getTodayLocalISODate(), []);

  const initialFormData: AdCampaignFormData = useMemo(() => {
    const shared = {
      placementId: "",
      placementName: "",
      creativeFile: null,
      creativePreviewUrl: mockCreativeThumb,
      countries: [],
      states: [],
      cities: [],
      pincodeCsvFile: null,
      pincodeCsvName: "",
    };

    if (mode !== "create") {
      return {
        ...shared,
        advertiserId: "",
        advertiserName: "",
        campaignName: "",
        pricingModel: "" as AdCampaignFormData["pricingModel"],
        pricingRate: "",
        dailyBudget: "",
        totalBudget: "",
        startDate: "",
        endDate: "",
        ctaText: "",
        landingUrl: "",
      };
    }

    return {
      ...shared,
      advertiserId: "",
      advertiserName: "",
      campaignName: "",
      pricingModel: "FLAT",
      pricingRate: "",
      dailyBudget: "",
      totalBudget: "",
      startDate: today,
      endDate: addDaysISO(30),
      ctaText: "",
      landingUrl: "",
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successTitle, setSuccessTitle] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoadingCampaign, setIsLoadingCampaign] = useState(mode !== "create");
  const [campaignLoadError, setCampaignLoadError] = useState<string | null>(null);
  const { functions, values } = useAdCampaignForm(initialFormData);
  const { handleFormChange } = functions;

  const { formData, validationErrors } = values;

  const readOnly = mode === "view";

  useEffect(() => {
    if (mode === "create" || !id) return;
    let cancelled = false;
    setIsLoadingCampaign(true);
    setCampaignLoadError(null);
    getAdCampaignById(id)
      .then((result) => {
        if (!cancelled) {
          handleFormChange(result.formData);
          setIsLoadingCampaign(false);
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          setCampaignLoadError(err?.message || "Failed to load campaign.");
          setIsLoadingCampaign(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id, mode, handleFormChange]);

  const pageTitle =
    mode === "view" ? "View Campaign" : mode === "edit" ? "Edit Campaign" : "Create Campaign";

  const handleBack = () => navigate("/ads/campaigns");

  const handleSaveDraft = useCallback(async () => {
    setError(null);
    try {
      const apiMessage = await functions.saveDraft();
      setSuccessTitle("Campaign Saved as Draft");
      setSuccessMessage(apiMessage || `Campaign "${formData.campaignName || "Untitled"}" saved as draft successfully.`);
      setShowSuccessModal(true);
    } catch (e: any) {
      setError(e?.message || "Failed to save campaign as draft.");
    }
  }, [functions, formData.campaignName]);

  const handleSubmitForApproval = useCallback(async () => {
    setError(null);
    try {
      const apiMessage = await functions.submitCampaign();
      setSuccessTitle("Campaign Submitted");
      setSuccessMessage(apiMessage || `Campaign "${formData.campaignName || "Untitled"}" sent for approval successfully.`);
      setShowSuccessModal(true);
    } catch (e: any) {
      setError(e?.message || "Failed to submit campaign for approval.");
    }
  }, [functions, formData.campaignName]);

  const handleEdit = useCallback(async () => {
    if (!id) return;
    const isValid = functions.validateStep(0);
    if (!isValid) return;
    setError(null);
    try {
      const result = await updateAdCampaign(id, formData);
      setSuccessTitle("Campaign Updated");
      setSuccessMessage(result.message || `Campaign "${formData.campaignName || "Untitled"}" updated successfully.`);
      setShowSuccessModal(true);
    } catch (e: any) {
      setError(e?.message || "Failed to update campaign.");
    }
  }, [id, functions, formData]);

  if (isLoadingCampaign) {
    return (
      <div className="page-content">
        <Container fluid>
          <ShimmerLoader type="form" rows={6} />
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <div className="mb-3" style={{ padding: "0 15px" }}>
          <Breadcrumbs
            items={[
              { label: "Ads", onClick: () => navigate("/ads/overview") },
              { label: "Campaigns", onClick: () => navigate("/ads/campaigns") },
              { label: pageTitle, active: true },
            ]}
          />
        </div>

        <div className="campaign-detail-header">
          <div className="campaign-detail-header-content">
            <button className="campaign-detail-back-button" onClick={handleBack} aria-label="Go back">
              <i className="ph-arrow-left" />
            </button>
            <div className="campaign-detail-title-section">
              <h1 className="campaign-detail-title">{pageTitle}</h1>
            </div>
          </div>
        </div>

        {(error || campaignLoadError) && (
          <div style={{ padding: "0 15px", marginBottom: 12 }}>
            <Alert
              variant="danger"
              dismissible
              onClose={() => {
                setError(null);
                setCampaignLoadError(null);
              }}
            >
              {error || campaignLoadError}
            </Alert>
          </div>
        )}

        <Row>
          <Col lg={8}>
            <Card>
              <Card.Body className="p-4">
                <StepBasicInfo
                  formData={formData}
                  validationErrors={validationErrors}
                  onChange={functions.handleFormChange}
                  onNext={handleSubmitForApproval}
                  onSaveDraft={mode === "create" ? handleSaveDraft : undefined}
                  onEdit={handleEdit}
                  onCancel={handleBack}
                  readOnly={readOnly}
                  mode={mode}
                />
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            <div className="position-sticky" style={{ top: 100 }}>
              <AdCampaignPreview formData={formData} />
            </div>
          </Col>
        </Row>
      </Container>

      <SuccessModal
        show={showSuccessModal}
        handleClose={() => {
          setShowSuccessModal(false);
          navigate("/ads/campaigns");
        }}
        title={successTitle}
        message={successMessage}
      />
    </div>
  );
};

export default CreateCampaign;
